import React, { useState } from 'react';
import FieldWrapper from './FieldWrapper';
import Image from '../../../../components/svg/Image';
import ESignatureModal from '../../Editors/ESignatureModal';
import { GetESignatureType, GetSignature, GetSignatureValue, HasAdoptedSignature } from '../../../esignatureHelper';
import { AppFeatures, canIUse } from '../../../../helpers/PermissionUtility';
import { showConfirm, showMessage } from '../../../../helpers/MessageUtility';
import { SasToken } from 'gf-types';
import { useAppSelector } from '../../../../hooks/ReduxHooks';
import { analyticsTrack } from '../../../../helpers/AnalyticsUtility';
import { FieldTypes } from '../../../../helpers/constants';
import { ESignFieldType, ValueChangedForFieldFunc } from '../../../formeditor-types';
import { Signature } from '../../../../profile/profile-types';

export interface ESignatureFieldProps {
  valueUpdated: ValueChangedForFieldFunc;
  id?: string;
  fieldId?: string;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  backgroundColor: TypeMePlease;
  padding?: number;
  editable: boolean;
  name?: string;
  value?: string;
  signingMethod?: number;
  fieldType?: ESignFieldType;
  formId?: string;
  templateName?: string;
  templateId?: string;
  align?: string;
  backgroundCropDimensions?: TypeMePlease;
}

export default function ESignatureField(props: ESignatureFieldProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const sasToken = useAppSelector<SasToken>((state) => state.shared.sasToken);
  const fileUrls = useAppSelector<Record<string, string>>((state) => state.formeditor.fileUrls);
  const isPublicForm = !!useAppSelector<boolean>((state) => !!state.formeditor.sharedAccessToken);
  const signatureType = GetESignatureType(props.fieldType);
  const userSignature = useAppSelector<Signature>(({ profile }) => (!isPublicForm && profile.savedSignatures ? profile.savedSignatures[signatureType] : null));
  const [selectFontMode, setSelectFontMode] = useState(false);

  const getFilePath = (fileId) => {
    if (fileUrls) {
      return fileUrls[fileId];
    }
    if (sasToken) {
      return `${sasToken.baseUrl}${fileId}${sasToken.token}`;
    }
    return null;
  };

  const mouseDown = () => {
    if (!canIUse(AppFeatures.eSignature)) {
      showMessage('Upgrade Required', 'This account needs to be upgraded to use eSign');
      return;
    }
    if (props.value) {
      showConfirm({ title: 'Confirm', msg: `Are you sure you want to clear this field?` }, (result) => {
        if (result) {
          props.valueUpdated(null);
          analyticsTrack('eSign Field is Cleared of Signature', {
            type: props.fieldType === FieldTypes.eSignature ? 'Signature' : 'Initials',
            view: 'Form View',
            templateId: props.templateId,
            templateName: props.templateName,
            fieldId: props.id,
            fieldName: props.name,
            isPublicForm: isPublicForm,
            isMobile: false
          });
        }
      });

      return;
    }
    if (HasAdoptedSignature(props.fieldType)) {
      // if any other eSignature field and the form has been filled out insert exsiting signature w/o opening modal
      props.valueUpdated({ value: JSON.stringify(GetSignatureValue(GetSignature(props.fieldType).files[0].fileId)) });
      analyticsTrack('eSign Field Filled', {
        type: props.fieldType === FieldTypes.eSignature ? 'Signature' : 'Initials',
        modalShown: false,
        view: 'Form View',
        templateId: props.templateId,
        templateName: props.templateName,
        fieldId: props.id,
        fieldName: props.name,
        isPublicForm: isPublicForm,
        isMobile: false
      });
    } else {
      setModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  let signature = null;
  let modal = null;

  if (props.value) {
    const properties = JSON.parse(props.value);
    const signatureUrl = getFilePath(properties.FileId);
    signature = <Image src={signatureUrl} align={props.align} padding={props.padding} w={props.w} h={props.h} shouldUpscale={true} />;
  }

  if (modalOpen) {
    modal = (
      <ESignatureModal
        name={props.name}
        valueUpdated={props.valueUpdated}
        onClose={handleModalClose}
        formId={props.formId}
        templateId={props.templateId}
        templateName={props.templateName}
        fieldId={props.fieldId}
        fieldType={props.fieldType}
        saveLocation={'Form Editor'}
        view={'Form View'}
        signature={userSignature}
        selectFontMode={selectFontMode}
        setSelectFontMode={setSelectFontMode}
      />
    );
  }
  return (
    <FieldWrapper
      onMouseDown={mouseDown}
      x={props.x}
      y={props.y}
      w={props.w}
      h={props.h}
      backgroundColor={props.backgroundColor}
      editable={props.editable}
      id={props.id}
      fieldId={props.fieldId}
    >
      {signature}
      {modal}
    </FieldWrapper>
  );
}
